import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { copiaDoFicheiro } from "../lib/copiaDoFicheiro";
import { mensagemDeErro } from "../lib/erroApi";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ScrollArea } from "../components/ui/scroll-area";
import { isFleetDomain, isFleetOnly } from "../config/productConfig";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { BookOpen as HelpBook } from "lucide-react";
import { helpPath } from "../help";
import RulesPanel from "./admin/RulesPanel";
import DiPanel from "./admin/DiPanel";
import AiStatsPanel from "./admin/AiStatsPanel";
import AerofleetPanel from "./admin/AerofleetPanel";
import TrainingDiagnosticsPanel from "./admin/TrainingDiagnosticsPanel";
import AdminNotificationBell from "../components/AdminNotificationBell";
import { FleetPaywall, FleetNotices, isLocked } from "../components/FleetAccount";
import { 
  Shield, LogOut, Users, BookOpen, FileText, Upload,
  AlertTriangle, Search, Edit, Trash2, Archive, RefreshCw,
  ChevronLeft, ChevronRight, CheckCircle, XCircle, Eye,
  GraduationCap, Rocket, Award, LayoutDashboard, Plus, Minus, Crown, Unlock, Lock, Edit2, Settings,
  School, Inbox, ClipboardCheck, Plane, Brain, CalendarCheck, PlaneTakeoff
} from "lucide-react";

const AdminDashboard = () => {
  const { user, logout, school, checkAuth } = useAuth();
  // O administrador também pode ser piloto ou instrutor: com a conta ligada a uma ficha, tem as reservas, o registo de
  // voos (só piloto) e os seus documentos, como na área de aluno
  const [minhaFicha, setMinhaFicha] = useState(null);
  useEffect(() => {
    axios.get(`${API}/di/documentos/me`, { withCredentials: true })
      .then((r) => setMinhaFicha({ kind: r.data?.kind }))
      .catch(() => setMinhaFicha(null));
  }, []);
  const isFleetOnlySchool = isFleetOnly(school);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [reports, setReports] = useState([]);
  const [sections, setSections] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Filters
  const [userSearch, setUserSearch] = useState("");
  const [questionSection, setQuestionSection] = useState("all");
  const [questionSearch, setQuestionSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [showReported, setShowReported] = useState(false);
  const [reportsFilter, setReportsFilter] = useState("pending"); // "all", "pending", "resolved", "dismissed"
  
  // School requests (Phase 4 multi-tenancy)
  const [schoolRequests, setSchoolRequests] = useState([]);
  const [schoolRequestsCount, setSchoolRequestsCount] = useState(0);
  
  // Super-admin: filter users by school
  const [userSchoolFilter, setUserSchoolFilter] = useState("all");
  const [allSchools, setAllSchools] = useState([]);
  const isSuperAdmin = user?.role === "super_admin";
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  
  // Section unlock modal
  const [unlockModalOpen, setUnlockModalOpen] = useState(false);
  const [selectedUserForUnlock, setSelectedUserForUnlock] = useState(null);
  const [userUnlockedSections, setUserUnlockedSections] = useState([]);
  const [modalSections, setModalSections] = useState([]);
  const [loadingUnlock, setLoadingUnlock] = useState(false);
  
  // Edit user name state
  const [showEditNameDialog, setShowEditNameDialog] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editingUserName, setEditingUserName] = useState("");
  const [savingUserName, setSavingUserName] = useState(false);
  
  const fileInputRef = useRef(null);

  const handleEditUserName = (userId, currentName) => {
    setEditingUserId(userId);
    setEditingUserName(currentName || "");
    setShowEditNameDialog(true);
  };

  const handleSaveUserName = async () => {
    if (!editingUserName.trim() || editingUserName.trim().length < 2) {
      toast.error("O nome deve ter pelo menos 2 caracteres");
      return;
    }
    
    setSavingUserName(true);
    try {
      await axios.put(
        `${API}/admin/users/${editingUserId}/name`, 
        { name: editingUserName.trim() }, 
        { withCredentials: true }
      );
      toast.success("Nome atualizado com sucesso");
      setShowEditNameDialog(false);
      fetchData();
    } catch (error) {
      toast.error(mensagemDeErro(error, "Erro ao atualizar nome"));
    } finally {
      setSavingUserName(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);
  
  // Force a valid tab for non-super admins (overview & reports are hidden).
  // Fleet-only (fleet school or fleet domain) only has the "di" tab, for super admins too.
  // The school arrives after the user, so this must also run when isFleetOnlySchool flips to true:
  // otherwise the first pass picks "overview" or "users" (not shown for fleet) and the page looks empty.
  useEffect(() => {
    if (!user) return;
    if (isFleetOnlySchool) {
      if (activeTab !== "di") setActiveTab("di");
    } else if (!isSuperAdmin && (activeTab === "overview" || activeTab === "reports")) {
      setActiveTab("users");
    }
  }, [user, isSuperAdmin, isFleetOnlySchool, activeTab]);

  const fetchData = async () => {
    try {
      const isSuper = user?.role === "super_admin";
      const [statsRes, usersRes, sectionsRes, reportsRes, classesRes, schoolReqRes, schoolReqCountRes, schoolsRes] = await Promise.all([
        // Overview/reports endpoints are super-admin only — silently ignore 403 for school admins
        (isSuper
          ? axios.get(`${API}/admin/stats`, { withCredentials: true })
          : Promise.resolve({ data: null })),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        (isSuper
          ? axios.get(`${API}/admin/sections`, { withCredentials: true })
          : Promise.resolve({ data: [] })),
        (isSuper
          ? axios.get(`${API}/admin/reports`, { withCredentials: true })
          : Promise.resolve({ data: [] })),
        axios.get(`${API}/admin/classes`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/school-requests`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/school-requests/count`, { withCredentials: true }).catch(() => ({ data: { count: 0 } })),
        // Super-admins need the global school list for the user filter
        (isSuper
          ? axios.get(`${API}/super-admin/schools`, { withCredentials: true }).catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] })),
      ]);
      
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setSections(sectionsRes.data);
      setReports(reportsRes.data);
      setClasses(classesRes.data);
      setSchoolRequests(schoolReqRes.data || []);
      setSchoolRequestsCount(schoolReqCountRes.data?.count || 0);
      setAllSchools(schoolsRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };
  
  const refreshSchoolRequests = async () => {
    try {
      const [r, c] = await Promise.all([
        axios.get(`${API}/admin/school-requests`, { withCredentials: true }),
        axios.get(`${API}/admin/school-requests/count`, { withCredentials: true }),
      ]);
      setSchoolRequests(r.data || []);
      setSchoolRequestsCount(c.data?.count || 0);
    } catch (e) {
      // silent
    }
  };
  
  const approveSchoolRequest = async (userId, classId = null) => {
    try {
      await axios.post(
        `${API}/admin/school-requests/${userId}/approve`,
        classId ? { class_id: classId } : {},
        { withCredentials: true },
      );
      toast.success("Pedido aprovado");
      await Promise.all([refreshSchoolRequests(), refreshUsers()]);
    } catch (e) {
      toast.error(mensagemDeErro(e, "Erro ao aprovar pedido"));
    }
  };
  
  const rejectSchoolRequest = async (userId) => {
    try {
      await axios.post(
        `${API}/admin/school-requests/${userId}/reject`,
        {},
        { withCredentials: true },
      );
      toast.success("Pedido rejeitado");
      await refreshSchoolRequests();
    } catch (e) {
      toast.error(mensagemDeErro(e, "Erro ao rejeitar pedido"));
    }
  };

  const fetchQuestions = async () => {
    try {
      const params = new URLSearchParams();
      if (questionSection !== "all") params.append("section", questionSection);
      // Default: active only. When toggled: archived only.
      params.append("archived", showArchived ? "true" : "false");
      if (showReported) params.append("reported", "true");
      
      const response = await axios.get(
        `${API}/admin/questions?${params.toString()}`,
        { withCredentials: true }
      );
      setQuestions(response.data);
      setCurrentPage(1);
    } catch (error) {
      toast.error("Erro ao carregar questões");
    }
  };

  useEffect(() => {
    if (activeTab === "questions") {
      fetchQuestions();
    }
  }, [activeTab, questionSection, showArchived, showReported]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      await axios.put(
        `${API}/admin/users/${userId}`,
        { role: newRole },
        { withCredentials: true }
      );
      toast.success("Utilizador atualizado");
      fetchData();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const status = error?.response?.status;
      toast.error(detail || "Erro ao atualizar utilizador");
      // Stale-cache repair: if the user_id is gone, refresh the list
      if (status === 404) fetchData();
    }
  };

  // Recarrega só a lista de utilizadores. fetchData() carrega a página toda (estatísticas, relatórios...) e demora.
  const refreshUsers = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(data || []);
    } catch {
      // silencioso: a lista volta a carregar ao recarregar a página
    }
  };

  const updateUserClass = async (userId, classId) => {
    const novaTurma = classId === "none" ? null : classId;
    try {
      await axios.put(
        `${API}/admin/users/${userId}`,
        { class_id: novaTurma },
        { withCredentials: true }
      );
      toast.success("Turma atualizada");
      // Mostra a alteração já, sem esperar pela lista
      setUsers((lista) => lista.map((u) => (u.user_id === userId ? { ...u, class_id: novaTurma } : u)));
      await refreshUsers();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || "Erro ao atualizar turma");
    }
  };

  const updateUserSchool = async (userId, schoolId) => {
    try {
      await axios.put(
        `${API}/admin/users/${userId}`,
        { school_id: schoolId },
        { withCredentials: true }
      );
      toast.success(schoolId ? "Escola atribuída" : "Escola removida");
      // O servidor também retira a turma ao mudar de escola: mostramos isso já, sem esperar pela lista
      setUsers((lista) => lista.map((u) => (u.user_id === userId ? { ...u, school_id: schoolId || null, class_id: null } : u)));
      await refreshUsers();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || "Erro ao atualizar escola");
    }
  };

  const deleteUser = async (userId) => {
    if (!confirm("Tem a certeza que deseja eliminar este utilizador?")) return;
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      toast.success("Utilizador eliminado");
      fetchData();
    } catch (error) {
      toast.error("Erro ao eliminar utilizador");
    }
  };

  const toggleUserSubscription = async (userId, hasSubscription) => {
    try {
      const endpoint = hasSubscription 
        ? `${API}/admin/users/${userId}/revoke-subscription`
        : `${API}/admin/users/${userId}/grant-subscription`;
      
      await axios.post(endpoint, {}, { withCredentials: true });
      toast.success(hasSubscription ? "Subscrição revogada" : "Subscrição atribuída");
      fetchData();
    } catch (error) {
      toast.error("Erro ao atualizar subscrição");
    }
  };

  // Section unlock management
  const openUnlockModal = async (u) => {
    setSelectedUserForUnlock(u);
    setLoadingUnlock(true);
    try {
      const response = await axios.get(
        `${API}/admin/users/${u.user_id}/unlocked-sections`,
        { withCredentials: true }
      );
      setUserUnlockedSections(response.data.unlocked_sections || []);
      setModalSections(response.data.sections || []);
      setUnlockModalOpen(true);
    } catch (error) {
      console.error("Error loading sections:", error);
      const errorMsg = error.response?.data?.detail || "Erro ao carregar disciplinas";
      toast.error(errorMsg);
    } finally {
      setLoadingUnlock(false);
    }
  };

  const toggleSectionUnlock = async (sectionId, isCurrentlyUnlocked) => {
    if (!selectedUserForUnlock) return;
    
    try {
      const endpoint = isCurrentlyUnlocked 
        ? `${API}/admin/users/${selectedUserForUnlock.user_id}/lock-sections`
        : `${API}/admin/users/${selectedUserForUnlock.user_id}/unlock-sections`;
      
      const response = await axios.post(
        endpoint,
        { section_ids: [sectionId] },
        { withCredentials: true }
      );
      
      setUserUnlockedSections(response.data.unlocked_sections || []);
      toast.success(isCurrentlyUnlocked ? "Disciplina bloqueada" : "Disciplina desbloqueada");
    } catch (error) {
      toast.error("Erro ao atualizar disciplina");
    }
  };

  const toggleAllSectionsForUser = async () => {
    if (!selectedUserForUnlock) return;
    
    // Check if all sections are unlocked (except we always have sec_legislacao)
    const allSectionIds = modalSections.map(s => s.section_id);
    const allUnlocked = allSectionIds.every(id => userUnlockedSections.includes(id));
    
    try {
      if (allUnlocked) {
        // Lock all (except sec_legislacao)
        const sectionsToLock = allSectionIds.filter(id => id !== "sec_legislacao");
        const response = await axios.post(
          `${API}/admin/users/${selectedUserForUnlock.user_id}/lock-sections`,
          { section_ids: sectionsToLock },
          { withCredentials: true }
        );
        setUserUnlockedSections(response.data.unlocked_sections || []);
        toast.success("Todas as disciplinas bloqueadas (excepto Legislação)");
      } else {
        // Unlock all
        const response = await axios.post(
          `${API}/admin/users/${selectedUserForUnlock.user_id}/unlock-sections`,
          { section_ids: allSectionIds },
          { withCredentials: true }
        );
        setUserUnlockedSections(response.data.unlocked_sections || []);
        toast.success("Todas as disciplinas desbloqueadas");
      }
    } catch (error) {
      toast.error("Erro ao atualizar disciplinas");
    }
  };

  const toggleQuestionArchive = async (questionId, archive) => {
    try {
      await axios.put(
        `${API}/admin/questions/${questionId}`,
        { is_archived: archive },
        { withCredentials: true }
      );
      toast.success(archive ? "Questão arquivada" : "Questão reativada");
      fetchQuestions();
      fetchData();
    } catch (error) {
      toast.error("Erro ao atualizar questão");
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      const formData = new FormData();
      formData.append("file", await copiaDoFicheiro(file));
      toast.info("A importar ficheiro...");
      const response = await axios.post(
        `${API}/admin/import`,
        formData,
        { 
          withCredentials: true
        }
      );
      
      toast.success(`Importadas ${response.data.imported} questões`);
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} erros durante importação`);
      }
      fetchData();
      fetchQuestions();
    } catch (error) {
      toast.error(mensagemDeErro(error, "Erro ao importar ficheiro"));
    }
    
    event.target.value = "";
  };

  const resolveReport = async (reportId, status) => {
    try {
      await axios.put(
        `${API}/admin/reports/${reportId}?status=${status}`,
        {},
        { withCredentials: true }
      );
      toast.success("Denúncia resolvida");
      fetchData();
    } catch (error) {
      toast.error("Erro ao resolver denúncia");
    }
  };

  // Role options shown in the role dropdown — depends on the acting user's role.
  // Backend enforces the same rule (admin.py:update_user blocks promotion to
  // super_admin unless caller is super_admin). This just hides the option.
  const roleOptions = isSuperAdmin
    ? [
        { value: "student", label: "Aluno" },
        { value: "instructor", label: "Instrutor" },
        { value: "admin", label: "Admin" },
        { value: "super_admin", label: "Super Admin" },
      ]
    : [
        { value: "student", label: "Aluno" },
        { value: "instructor", label: "Instrutor" },
        { value: "admin", label: "Admin" },
      ];
  
  // Filter users
  const filteredUsers = users.filter(u => {
    const matchesSearch = u.name.toLowerCase().includes(userSearch.toLowerCase()) ||
      u.email.toLowerCase().includes(userSearch.toLowerCase());
    if (!matchesSearch) return false;
    if (isSuperAdmin && userSchoolFilter !== "all") {
      if (userSchoolFilter === "none") return !u.school_id;
      return u.school_id === userSchoolFilter;
    }
    return true;
  });

  // Filter questions by search text
  const filteredQuestions = questions.filter((q) => {
    if (!questionSearch) return true;
    const searchLower = questionSearch.toLowerCase();
    // Search in statement, question_id, and answers
    const inStatement = q.statement?.toLowerCase().includes(searchLower);
    const inId = q.question_id?.toLowerCase().includes(searchLower);
    // Handle both old format (strings) and new format (objects with id/text)
    const inAnswers = q.answers?.some(a => {
      const ansText = typeof a === 'object' ? a?.text : a;
      return ansText?.toLowerCase().includes(searchLower);
    });
    const inNote = q.note?.toLowerCase().includes(searchLower);
    return inStatement || inId || inAnswers || inNote;
  });

  // Pagination
  const totalPages = Math.ceil(filteredQuestions.length / itemsPerPage);
  const paginatedQuestions = filteredQuestions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-cockpit-bg flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (isLocked(school)) {
    return <FleetPaywall school={school} onLogout={handleLogout} />;
  }

  return (
    <div className="min-h-screen bg-cockpit-bg">
      {/* Header */}
      <header className="border-b border-cockpit-border bg-cockpit-panel/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-3 sm:px-6 py-2 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4">
              <div className={`w-8 h-8 sm:w-10 sm:h-10 ${isFleetDomain() ? "bg-orange-500" : "bg-red-500"} rounded-lg flex items-center justify-center`}>
                <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div>
                <h1 className="font-chivo text-sm sm:text-lg text-cockpit-text-bright">{school?.name || (isFleetDomain() ? "Aerofleet Center" : "ULM Training Centre")}</h1>
                <p className="text-[10px] sm:text-xs text-slate-500 font-mono hidden sm:block">Administração</p>
              </div>
            </div>
            
            <div className="flex items-center gap-1 sm:gap-2">
              {user?.role === "super_admin" && (
                <Button
                  size="sm"
                  onClick={() => navigate("/super-admin/schools")}
                  className="bg-purple-600 hover:bg-purple-500 text-white text-xs p-1.5 sm:p-2"
                  title="Super-Admin · Escolas"
                  data-testid="super-admin-link"
                >
                  <Shield className="w-4 h-4 sm:mr-1" />
                  <span className="hidden sm:inline">Super</span>
                </Button>
              )}
              {!isFleetOnlySchool && (
  <>
              {/* Navigation buttons */}
              <Button 
                variant="ghost"
                size="sm"
                onClick={() => navigate("/dashboard")}
                className="text-slate-400 hover:text-emerald-400 text-xs p-1.5 sm:p-2"
                title="Dashboard Aluno"
              >
                <LayoutDashboard className="w-4 h-4" />
              </Button>
              <Button 
                variant="ghost"
                size="sm"
                onClick={() => navigate("/instructor")}
                className="text-slate-400 hover:text-cyan-400 text-xs p-1.5 sm:p-2"
                title="Dashboard Instrutor"
              >
                <GraduationCap className="w-4 h-4" />
              </Button>
              <Button 
                variant="ghost"
                size="sm"
                onClick={() => navigate("/missions")}
                className="text-slate-400 hover:text-purple-400 text-xs p-1.5 sm:p-2 hidden sm:flex"
                title="Missões"
              >
                <Rocket className="w-4 h-4" />
              </Button>
              <Button 
                variant="ghost"
                size="sm"
                onClick={() => navigate("/badges")}
                className="text-slate-400 hover:text-yellow-400 text-xs p-1.5 sm:p-2 hidden sm:flex"
                title="Badges"
              >
                <Award className="w-4 h-4" />
              </Button>
</>
)}

              {minhaFicha && (
                <>
                  <Button variant="ghost" size="sm" onClick={() => navigate("/reservas")} className="text-slate-400 hover:text-sky-400 text-xs p-1.5 sm:p-2" title="As minhas reservas" data-testid="admin-minhas-reservas">
                    <CalendarCheck className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => navigate("/registar-voo")} className="text-slate-400 hover:text-sky-400 text-xs p-1.5 sm:p-2" title="Registar voo" data-testid="admin-registar-voo">
                    <PlaneTakeoff className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => navigate("/meus-documentos")} className="text-slate-400 hover:text-sky-400 text-xs p-1.5 sm:p-2" title="Os meus documentos" data-testid="admin-meus-documentos">
                    <FileText className="w-4 h-4" />
                  </Button>
                </>
              )}
              {/* Notification bell — DI alerts + reported questions */}
              <AdminNotificationBell
                userId={user?.user_id}
                onNavigateToReports={() => setActiveTab("reports")}
                onNavigateToDi={() => setActiveTab("di")}
              />

              <div className="w-px h-4 sm:h-6 bg-slate-700 mx-1 sm:mx-2" />
              
              {/* User section - Avatar dropdown on mobile, full info on desktop */}
              <div className="flex items-center gap-2 sm:gap-3">
                {/* Desktop: show name and role */}
                <div className="text-right hidden sm:block">
                  <p className="text-sm text-cockpit-text-bright">{user?.name}</p>
                  <p className={`text-xs font-mono ${isSuperAdmin ? "text-purple-400" : "text-red-400"}`} data-testid="header-role-label">
                    {isSuperAdmin ? "Super Admin" : "Administrador"}
                  </p>
                </div>
                
                {/* Avatar with dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full overflow-hidden border-2 border-slate-600 transition-colors focus:outline-none focus:ring-2 ${isFleetDomain() ? "hover:border-orange-500 focus:ring-orange-500/50" : "hover:border-red-500 focus:ring-red-500/50"}`}>
                      {user?.picture ? (
                        <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className={`w-full h-full ${isFleetDomain() ? "bg-orange-500" : "bg-red-500"} flex items-center justify-center text-white font-bold text-sm`}>
                          {user?.name?.charAt(0)?.toUpperCase() || 'A'}
                        </div>
                      )}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-slate-900 border-slate-700 min-w-[200px]">
                    <div className="px-3 py-2 border-b border-slate-700">
                      <p className="text-sm text-cockpit-text-bright font-medium">{user?.name}</p>
                      <p className={`text-xs ${isSuperAdmin ? "text-purple-400" : "text-red-400"}`}>
                        {isSuperAdmin ? "Super Admin" : "Administrador"}
                      </p>
                    </div>
                    {!isFleetOnlySchool && (
                    <div className="sm:hidden py-1">
                      <DropdownMenuItem onClick={() => navigate("/missions")} className="text-slate-300 hover:text-white cursor-pointer">
                        <Rocket className="w-4 h-4 mr-2 text-purple-400" />
                        Missões
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/badges")} className="text-slate-300 hover:text-white cursor-pointer">
                        <Award className="w-4 h-4 mr-2 text-yellow-400" />
                        Badges
                      </DropdownMenuItem>
                    </div>
                    )}
                    <DropdownMenuItem onClick={() => navigate(helpPath("administrador"))} className="text-slate-300 hover:text-white cursor-pointer" data-testid="menu-help">
                      <HelpBook className="w-4 h-4 mr-2 text-sky-400" />
                      Manual de ajuda
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleLogout} className="text-red-400 hover:text-red-300 cursor-pointer">
                      <LogOut className="w-4 h-4 mr-2" />
                      Terminar Sessão
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8">
        <FleetNotices school={school} onSaved={checkAuth} />
        {/* Pending school requests banner */}
        {!isFleetOnlySchool && schoolRequestsCount > 0 && activeTab !== "school-requests" && (
          <div
            className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-center justify-between gap-3 cursor-pointer hover:bg-amber-500/15 transition-colors"
            onClick={() => setActiveTab("school-requests")}
            data-testid="banner-pending-school-requests"
          >
            <div className="flex items-center gap-3">
              <Inbox className="w-5 h-5 text-amber-400 shrink-0" />
              <div>
                <p className="text-amber-300 font-medium">
                  {schoolRequestsCount === 1
                    ? "1 pedido pendente"
                    : `${schoolRequestsCount} pedidos pendentes`} de associação a escola
                </p>
                <p className="text-sm text-slate-400">Carrega aqui para rever e aprovar.</p>
              </div>
            </div>
            <Button
              size="sm"
              className="btn-cockpit shrink-0"
              onClick={(e) => { e.stopPropagation(); setActiveTab("school-requests"); }}
            >
              Ver pedidos
            </Button>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-cockpit-panel border border-cockpit-border flex flex-wrap h-auto gap-1 p-1 justify-start">
            {isSuperAdmin && (
              <TabsTrigger value="overview" className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-400">
                Visão Geral
              </TabsTrigger>
            )}
            {!isFleetOnlySchool && (
              <TabsTrigger value="users" className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-400">
                Utilizadores
              </TabsTrigger>
            )}
            {isSuperAdmin && (
              <TabsTrigger value="questions" className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-400">
                Questões
              </TabsTrigger>
            )}
            {isSuperAdmin && (
              <TabsTrigger value="reports" className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-400">
                Denúncias
                {stats?.pending_reports > 0 && (
                  <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                    {stats.pending_reports}
                  </span>
                )}
              </TabsTrigger>
            )}
            {isSuperAdmin && (
              <TabsTrigger value="rules" className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-400" data-testid="tab-rules">
                Regras
              </TabsTrigger>
            )}
            <TabsTrigger value="di" className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-400" data-testid="tab-di">
              <Plane className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Direção de Instrução</span>
              <span className="sm:hidden">DI</span>
            </TabsTrigger>
            {!isFleetOnlySchool && (
              <TabsTrigger value="school-requests" className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-400" data-testid="tab-school-requests">
                Pedidos
                {schoolRequestsCount > 0 && (
                  <span className="ml-2 px-2 py-0.5 bg-amber-500 text-white text-xs rounded-full" data-testid="school-requests-badge">
                    {schoolRequestsCount}
                  </span>
                )}
              </TabsTrigger>
            )}
            {isSuperAdmin && (
              <TabsTrigger value="aerofleet" className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-400" data-testid="tab-aerofleet">
                <Plane className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Aerofleet</span>
              </TabsTrigger>
            )}
            {isSuperAdmin && (
              <TabsTrigger value="diagnostics" className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-400" data-testid="tab-diagnostics">
                <Search className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Diagnóstico</span>
              </TabsTrigger>
            )}
            {isSuperAdmin && (
              <TabsTrigger value="ia" className="data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-400" data-testid="tab-ia">
                <Brain className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">IA</span>
              </TabsTrigger>
            )}
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard icon={<Users />} value={stats?.total_users || 0} label="Total Utilizadores" color="cyan" onClick={() => setActiveTab("users")} />
              <StatCard icon={<BookOpen />} value={stats?.total_questions || 0} label="Questões Ativas" color="orange" onClick={() => setActiveTab("questions")} />
              <StatCard icon={<Archive />} value={stats?.archived_questions || 0} label="Arquivadas" color="slate" onClick={() => { setActiveTab("questions"); setShowArchived(true); }} />
              <StatCard icon={<AlertTriangle />} value={stats?.pending_reports || 0} label="Denúncias Pendentes" color="red" onClick={() => setActiveTab("reports")} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="instrument-panel cursor-pointer hover:border-slate-600 transition-colors" onClick={() => setActiveTab("users")}>
                <CardContent className="p-6">
                  <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Alunos</div>
                  <div className="font-mono text-3xl text-emerald-400">{stats?.total_students || 0}</div>
                </CardContent>
              </Card>
              <Card className="instrument-panel cursor-pointer hover:border-slate-600 transition-colors" onClick={() => setActiveTab("users")}>
                <CardContent className="p-6">
                  <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Instrutores</div>
                  <div className="font-mono text-3xl text-cyan-400">{stats?.total_instructors || 0}</div>
                </CardContent>
              </Card>
              <Card className="instrument-panel cursor-pointer hover:border-slate-600 transition-colors" onClick={() => { setActiveTab("questions"); setShowReported(true); }}>
                <CardContent className="p-6">
                  <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Questões Reportadas</div>
                  <div className="font-mono text-3xl text-yellow-400">{stats?.reported_questions || 0}</div>
                </CardContent>
              </Card>
            </div>

            {/* Import Section (super-admin only) */}
            {isSuperAdmin && (
            <Card className="instrument-panel">
              <CardHeader>
                <CardTitle className="font-chivo text-lg flex items-center gap-2">
                  <Upload className="w-5 h-5 text-orange-400" />
                  Importar Questões
                </CardTitle>
              </CardHeader>
              <CardContent>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImport}
                  accept=".xlsx,.xls"
                  className="hidden"
                />
                <Button 
                  onClick={() => fileInputRef.current?.click()}
                  className="btn-cockpit"
                  data-testid="import-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Selecionar Ficheiro Excel
                </Button>
                <p className="text-xs text-slate-500 mt-2">
                  Formato: ID Secção, Nome Secção, ID Tema, Nome Tema, ID Questão, Enunciado, Imagem, Resposta 0-3, Correta, Erro, Nota
                </p>
              </CardContent>
            </Card>
            )}

            {/* Maintenance Section */}
            <Card className="instrument-panel">
              <CardHeader>
                <CardTitle className="font-chivo text-lg flex items-center gap-2">
                  <Settings className="w-5 h-5 text-slate-400" />
                  Manutenção
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-300">Limpar Assignments Órfãos</p>
                    <p className="text-xs text-slate-500">Remove inscrições de exames onde o resultado foi eliminado</p>
                  </div>
                  <Button 
                    onClick={async () => {
                      if (!confirm("Tem a certeza que deseja limpar os assignments órfãos?")) return;
                      try {
                        const res = await axios.post(`${API}/admin/cleanup-orphan-assignments`, {}, { withCredentials: true });
                        toast.success(res.data.message);
                        if (res.data.details?.length > 0) {
                          console.log("Assignments eliminados:", res.data.details);
                        }
                      } catch (error) {
                        toast.error("Erro ao limpar assignments");
                      }
                    }}
                    variant="outline"
                    className="btn-secondary-cockpit"
                    data-testid="cleanup-orphans-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Executar Limpeza
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-6">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input 
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  placeholder="Pesquisar utilizadores..."
                  className="input-cockpit pl-10"
                  data-testid="user-search"
                />
              </div>
              {isSuperAdmin && allSchools.length > 0 && (
                <div className="flex items-center gap-2">
                  <School className="w-4 h-4 text-purple-400" />
                  <Select value={userSchoolFilter} onValueChange={setUserSchoolFilter}>
                    <SelectTrigger className="w-[220px] bg-slate-900 border-slate-700" data-testid="user-school-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      <SelectItem value="all">Todas as escolas</SelectItem>
                      <SelectItem value="none">Sem escola (pendentes)</SelectItem>
                      {allSchools.map((s) => (
                        <SelectItem key={s.school_id} value={s.school_id}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <Card className="instrument-panel">
              {/* Desktop Table View */}
              <div className="hidden md:block">
                <ScrollArea className="h-[500px]">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-cockpit-border">
                        <TableHead className="text-slate-400">Nome</TableHead>
                        <TableHead className="text-slate-400">Email</TableHead>
                        {isSuperAdmin && <TableHead className="text-slate-400">Escola</TableHead>}
                        <TableHead className="text-slate-400">Função</TableHead>
                        <TableHead className="text-slate-400">Turma</TableHead>
                        <TableHead className="text-slate-400">Subscrição</TableHead>
                        <TableHead className="text-slate-400">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredUsers.map((u) => (
                        <TableRow key={u.user_id} className="border-cockpit-border">
                          <TableCell className="text-cockpit-text-bright">
                            <div className="flex items-center gap-2">
                              <span>{u.name}</span>
                              <button
                                onClick={() => handleEditUserName(u.user_id, u.name)}
                                className="p-0.5 hover:bg-slate-700 rounded text-slate-500 hover:text-orange-400 transition-colors"
                                title="Editar nome"
                              >
                                <Edit2 className="w-3 h-3" />
                              </button>
                            </div>
                          </TableCell>
                          <TableCell className="text-slate-400 font-mono text-sm">{u.email}</TableCell>
                          {isSuperAdmin && (
                            <TableCell>
                              <Select
                                value={u.school_id || "none"}
                                onValueChange={(v) => updateUserSchool(u.user_id, v === "none" ? null : v)}
                              >
                                <SelectTrigger
                                  className={`w-44 h-8 bg-slate-900 border-slate-700 text-xs ${
                                    !u.school_id ? "text-amber-300" : ""
                                  }`}
                                  data-testid={`user-school-select-${u.user_id}`}
                                >
                                  <SelectValue placeholder="Sem escola" />
                                </SelectTrigger>
                                <SelectContent className="bg-slate-900 border-slate-700">
                                  <SelectItem value="none">
                                    {u.requested_school_id ? "Sem escola (Pendente)" : "Sem escola"}
                                  </SelectItem>
                                  {allSchools.map((s) => (
                                    <SelectItem key={s.school_id} value={s.school_id}>
                                      {s.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                          )}
                          <TableCell>
                            <Select 
                              value={u.role} 
                              onValueChange={(v) => updateUserRole(u.user_id, v)}
                              disabled={
                                (u.user_id === user?.user_id && u.role === "super_admin") ||
                                (!isSuperAdmin && u.role === "super_admin")
                              }
                            >
                              <SelectTrigger className="w-36 h-8 bg-slate-900 border-slate-700">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-slate-900 border-slate-700">
                                {roleOptions.map((opt) => (
                                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            {u.role === "student" ? (
                              <Select 
                                value={u.class_id || "none"} 
                                onValueChange={(v) => updateUserClass(u.user_id, v)}
                                disabled={!u.school_id}
                              >
                                <SelectTrigger className="w-40 h-8 bg-slate-900 border-slate-700">
                                  <SelectValue placeholder={u.school_id ? "Sem turma" : "Sem escola"} />
                                </SelectTrigger>
                                <SelectContent className="bg-slate-900 border-slate-700">
                                  <SelectItem value="none">Sem turma</SelectItem>
                                  {classes
                                    .filter((c) => !u.school_id || !c.school_id || c.school_id === u.school_id)
                                    .map((c) => (
                                      <SelectItem key={c.class_id} value={c.class_id}>
                                        {c.name}
                                      </SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <span className="text-slate-500 text-sm">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {/* Show subscription status badge */}
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                u.subscription_status === "admin_granted" 
                                  ? "bg-purple-500/20 text-purple-400 border border-purple-500/30" 
                                  : u.subscription_status === "premium"
                                    ? "bg-yellow-500/20 text-yellow-400 border border-yellow-500/30"
                                    : u.subscription_status === "trial"
                                      ? "bg-blue-500/20 text-blue-400 border border-blue-500/30"
                                      : u.subscription_status === "expired"
                                        ? "bg-red-500/20 text-red-400 border border-red-500/30"
                                        : "bg-slate-500/20 text-slate-400 border border-slate-500/30"
                              }`}>
                                {u.subscription_status === "admin_granted" && "Admin"}
                                {u.subscription_status === "premium" && `Premium (${u.subscription_days_remaining}d)`}
                                {u.subscription_status === "trial" && `Trial (${u.trial_days_remaining}d)`}
                                {u.subscription_status === "expired" && "Expirado"}
                                {u.subscription_status === "free" && "Grátis"}
                              </span>
                              
                              {/* Button to grant/revoke admin subscription (super-admin only, not paid premium) */}
                              {isSuperAdmin && u.subscription_status !== "premium" && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => toggleUserSubscription(u.user_id, u.subscription_admin_granted)}
                                  className={`h-6 px-2 ${
                                    u.subscription_admin_granted 
                                      ? "text-purple-400 hover:text-purple-300" 
                                      : "text-slate-500 hover:text-purple-400"
                                  }`}
                                  title={u.subscription_admin_granted ? "Revogar acesso admin" : "Dar acesso ilimitado"}
                                >
                                  <Crown className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {u.role === "student" && (
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  onClick={() => openUnlockModal(u)}
                                  disabled={loadingUnlock && selectedUserForUnlock?.user_id === u.user_id}
                                  className="text-slate-400 hover:text-cyan-400"
                                  title="Gerir disciplinas desbloqueadas"
                                >
                                  {loadingUnlock && selectedUserForUnlock?.user_id === u.user_id ? (
                                    <div className="w-4 h-4 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
                                  ) : (
                                    <Unlock className="w-4 h-4" />
                                  )}
                                </Button>
                              )}
                              {isSuperAdmin && (
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  onClick={() => deleteUser(u.user_id)}
                                  disabled={u.user_id === user?.user_id}
                                  className="text-slate-400 hover:text-red-400"
                                  data-testid={`delete-user-${u.user_id}`}
                                  title="Eliminar utilizador (super-admin)"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden">
                <ScrollArea className="h-[500px]">
                  <div className="space-y-3 p-3">
                    {filteredUsers.map((u) => (
                      <div key={u.user_id} className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                        {/* User Header */}
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-cockpit-text-bright font-medium truncate">{u.name}</p>
                              <button
                                onClick={() => handleEditUserName(u.user_id, u.name)}
                                className="p-0.5 hover:bg-slate-700 rounded text-slate-500 hover:text-orange-400 transition-colors"
                                title="Editar nome"
                              >
                                <Edit2 className="w-3 h-3" />
                              </button>
                            </div>
                            <p className="text-slate-400 text-xs truncate">{u.email}</p>
                            {isSuperAdmin && (u.school_name || u.requested_school_id) && (
                              <span className={`inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded-full ${
                                u.school_name
                                  ? "bg-purple-500/10 text-purple-300 border border-purple-500/30"
                                  : "bg-amber-500/10 text-amber-300 border border-amber-500/30"
                              }`}>
                                {u.school_name || "Pendente"}
                              </span>
                            )}
                          </div>
                          {/* Subscription Badge */}
                          <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${
                            u.subscription_status === "admin_granted" 
                              ? "bg-purple-500/20 text-purple-400 border border-purple-500/30" 
                              : u.subscription_status === "premium"
                                ? "bg-yellow-500/20 text-yellow-400 border border-yellow-500/30"
                                : u.subscription_status === "trial"
                                  ? "bg-blue-500/20 text-blue-400 border border-blue-500/30"
                                  : u.subscription_status === "expired"
                                    ? "bg-red-500/20 text-red-400 border border-red-500/30"
                                    : "bg-slate-500/20 text-slate-400 border border-slate-500/30"
                          }`}>
                            {u.subscription_status === "admin_granted" && "Admin"}
                            {u.subscription_status === "premium" && `Premium`}
                            {u.subscription_status === "trial" && `Trial (${u.trial_days_remaining}d)`}
                            {u.subscription_status === "expired" && "Expirado"}
                            {u.subscription_status === "free" && "Grátis"}
                          </span>
                        </div>

                        {/* User Controls */}
                        <div className="space-y-2">
                          {/* Role Selector */}
                          <div className="flex items-center justify-between">
                            <span className="text-slate-500 text-sm">Função:</span>
                            <Select 
                              value={u.role} 
                              onValueChange={(v) => updateUserRole(u.user_id, v)}
                              disabled={
                                (u.user_id === user?.user_id && u.role === "super_admin") ||
                                (!isSuperAdmin && u.role === "super_admin")
                              }
                            >
                              <SelectTrigger className="w-36 h-8 bg-slate-900 border-slate-700 text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-slate-900 border-slate-700">
                                {roleOptions.map((opt) => (
                                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* School Selector (super-admin only) */}
                          {isSuperAdmin && (
                            <div className="flex items-center justify-between">
                              <span className="text-slate-500 text-sm">Escola:</span>
                              <Select
                                value={u.school_id || "none"}
                                onValueChange={(v) => updateUserSchool(u.user_id, v === "none" ? null : v)}
                              >
                                <SelectTrigger
                                  className={`w-40 h-8 bg-slate-900 border-slate-700 text-sm ${!u.school_id ? "text-amber-300" : ""}`}
                                  data-testid={`user-school-select-mobile-${u.user_id}`}
                                >
                                  <SelectValue placeholder="Sem escola" />
                                </SelectTrigger>
                                <SelectContent className="bg-slate-900 border-slate-700">
                                  <SelectItem value="none">
                                    {u.requested_school_id ? "Sem escola (Pendente)" : "Sem escola"}
                                  </SelectItem>
                                  {allSchools.map((s) => (
                                    <SelectItem key={s.school_id} value={s.school_id}>
                                      {s.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}

                          {/* Class Selector (only for students) */}
                          {u.role === "student" && (
                            <div className="flex items-center justify-between">
                              <span className="text-slate-500 text-sm">Turma:</span>
                              <Select 
                                value={u.class_id || "none"} 
                                onValueChange={(v) => updateUserClass(u.user_id, v)}
                                disabled={!u.school_id}
                              >
                                <SelectTrigger className="w-32 h-8 bg-slate-900 border-slate-700 text-sm">
                                  <SelectValue placeholder={u.school_id ? "Sem turma" : "Sem escola"} />
                                </SelectTrigger>
                                <SelectContent className="bg-slate-900 border-slate-700">
                                  <SelectItem value="none">Sem turma</SelectItem>
                                  {classes
                                    .filter((c) => !u.school_id || !c.school_id || c.school_id === u.school_id)
                                    .map((c) => (
                                      <SelectItem key={c.class_id} value={c.class_id}>
                                        {c.name}
                                      </SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-slate-700">
                          {/* Grant/Revoke Subscription (super-admin only) */}
                          {isSuperAdmin && u.subscription_status !== "premium" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleUserSubscription(u.user_id, u.subscription_admin_granted)}
                              className={`h-8 px-3 ${
                                u.subscription_admin_granted 
                                  ? "text-purple-400 hover:text-purple-300" 
                                  : "text-slate-500 hover:text-purple-400"
                              }`}
                            >
                              <Crown className="w-4 h-4 mr-1" />
                              {u.subscription_admin_granted ? "Revogar" : "Premium"}
                            </Button>
                          )}
                          
                          {/* Unlock Disciplines */}
                          {u.role === "student" && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => openUnlockModal(u)}
                              disabled={loadingUnlock && selectedUserForUnlock?.user_id === u.user_id}
                              className="h-8 px-3 text-slate-400 hover:text-cyan-400"
                            >
                              {loadingUnlock && selectedUserForUnlock?.user_id === u.user_id ? (
                                <div className="w-4 h-4 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <>
                                  <Unlock className="w-4 h-4 mr-1" />
                                  Disciplinas
                                </>
                              )}
                            </Button>
                          )}
                          
                          {/* Delete User (super-admin only) */}
                          {isSuperAdmin && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => deleteUser(u.user_id)}
                              disabled={u.user_id === user?.user_id}
                              className="h-8 px-3 text-slate-400 hover:text-red-400"
                              data-testid={`delete-user-mobile-${u.user_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            </Card>
          </TabsContent>

          {/* Questions Tab */}
          <TabsContent value="questions" className="space-y-6">
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
              <Input
                placeholder="Pesquisar questões por texto, ID ou resposta..."
                value={questionSearch}
                onChange={(e) => {
                  setQuestionSearch(e.target.value);
                  setCurrentPage(1);
                }}
                className="input-cockpit pl-10 w-full"
                data-testid="question-search"
              />
              {questionSearch && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setQuestionSearch("")}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-white"
                >
                  <XCircle className="w-4 h-4" />
                </Button>
              )}
            </div>

            {/* Filters Row */}
            <div className="flex flex-wrap items-center gap-4">
              <Select value={questionSection} onValueChange={(v) => { setQuestionSection(v); setCurrentPage(1); }}>
                <SelectTrigger className="w-64 input-cockpit" data-testid="section-filter">
                  <SelectValue placeholder="Todas as secções" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="all">Todas as Secções</SelectItem>
                  {[...sections]
                    .sort((a, b) => (a.section_name || '').localeCompare(b.section_name || ''))
                    .map((s) => (
                      <SelectItem key={s.section_id} value={s.section_id}>
                        {s.section_name} ({s.count})
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              
              <Button 
                variant={showArchived ? "default" : "outline"}
                onClick={() => { setShowArchived(!showArchived); setCurrentPage(1); }}
                className={showArchived ? "btn-cockpit" : "btn-secondary-cockpit"}
              >
                <Archive className="w-4 h-4 mr-2" />
                Arquivadas
              </Button>
              
              <Button 
                variant={showReported ? "default" : "outline"}
                onClick={() => { setShowReported(!showReported); setCurrentPage(1); }}
                className={showReported ? "btn-cockpit" : "btn-secondary-cockpit"}
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Reportadas
              </Button>
              
              {/* Create New Question Button */}
              <QuestionCreateDialog 
                sections={sections}
                onCreated={() => { fetchQuestions(); fetchData(); }}
              />
              
              <div className="ml-auto flex items-center gap-4">
                <span className="text-sm text-slate-500">
                  {filteredQuestions.length} questões encontradas
                </span>
              </div>
            </div>

            <Card className="instrument-panel">
              <ScrollArea className="h-[500px]">
                {/* Desktop Table View */}
                <div className="hidden md:block">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-cockpit-border">
                        <TableHead className="text-slate-400 w-16">ID</TableHead>
                        <TableHead className="text-slate-400">Enunciado</TableHead>
                        <TableHead className="text-slate-400">Secção</TableHead>
                        <TableHead className="text-slate-400 w-24">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedQuestions.map((q) => (
                        <TableRow key={q.question_id} className="border-cockpit-border">
                          <TableCell className="font-mono text-xs text-slate-500">{q.question_id.slice(0, 10)}</TableCell>
                          <TableCell className="text-cockpit-text-bright max-w-md truncate">
                            {q.statement}
                          </TableCell>
                          <TableCell className="text-sm text-slate-400">{q.section_name}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <QuestionViewDialog question={q} />
                              <QuestionEditDialog 
                                question={q} 
                                sections={sections}
                                onSave={fetchQuestions}
                                onDelete={() => { fetchQuestions(); fetchData(); }}
                              />
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => toggleQuestionArchive(q.question_id, !q.is_archived)}
                                className="text-slate-400 hover:text-orange-400"
                                title={q.is_archived ? "Reativar" : "Arquivar"}
                              >
                                {q.is_archived ? <RefreshCw className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                
                {/* Mobile Card View */}
                <div className="md:hidden p-3 space-y-3">
                  {paginatedQuestions.map((q) => (
                    <div key={q.question_id} className="bg-slate-800/50 rounded-lg p-3 border border-slate-700">
                      {/* Header with ID and Actions */}
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-mono text-xs text-slate-500">{q.question_id.slice(0, 10)}</span>
                        <div className="flex gap-1">
                          <QuestionViewDialog question={q} />
                          <QuestionEditDialog 
                            question={q} 
                            sections={sections}
                            onSave={fetchQuestions}
                            onDelete={() => { fetchQuestions(); fetchData(); }}
                          />
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => toggleQuestionArchive(q.question_id, !q.is_archived)}
                            className="text-slate-400 hover:text-orange-400 h-8 w-8"
                            title={q.is_archived ? "Reativar" : "Arquivar"}
                          >
                            {q.is_archived ? <RefreshCw className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                          </Button>
                        </div>
                      </div>
                      {/* Statement */}
                      <p className="text-sm text-cockpit-text-bright line-clamp-2 mb-2">{q.statement}</p>
                      {/* Section Badge */}
                      <span className="text-xs bg-slate-700 text-slate-400 px-2 py-1 rounded">{q.section_name}</span>
                    </div>
                  ))}
                </div>
              </ScrollArea>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between p-4 border-t border-cockpit-border">
                  <div className="text-sm text-slate-500">
                    Página {currentPage} de {totalPages}
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="btn-secondary-cockpit"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="btn-secondary-cockpit"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="space-y-6">
            <Card className="instrument-panel">
              {/* Filter Buttons */}
              <div className="p-4 border-b border-cockpit-border flex gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant={reportsFilter === "pending" ? "default" : "outline"}
                  onClick={() => setReportsFilter("pending")}
                  className={reportsFilter === "pending" ? "btn-cockpit" : "btn-secondary-cockpit"}
                >
                  Pendentes ({reports.filter(r => r.status === 'pending').length})
                </Button>
                <Button
                  size="sm"
                  variant={reportsFilter === "resolved" ? "default" : "outline"}
                  onClick={() => setReportsFilter("resolved")}
                  className={reportsFilter === "resolved" ? "btn-cockpit" : "btn-secondary-cockpit"}
                >
                  Resolvidas ({reports.filter(r => r.status === 'resolved').length})
                </Button>
                <Button
                  size="sm"
                  variant={reportsFilter === "dismissed" ? "default" : "outline"}
                  onClick={() => setReportsFilter("dismissed")}
                  className={reportsFilter === "dismissed" ? "btn-cockpit" : "btn-secondary-cockpit"}
                >
                  Dispensadas ({reports.filter(r => r.status === 'dismissed').length})
                </Button>
                <Button
                  size="sm"
                  variant={reportsFilter === "all" ? "default" : "outline"}
                  onClick={() => setReportsFilter("all")}
                  className={reportsFilter === "all" ? "btn-cockpit" : "btn-secondary-cockpit"}
                >
                  Todas ({reports.length})
                </Button>
              </div>
              
              <ScrollArea className="h-[450px]">
                {reports.filter(r => reportsFilter === "all" || r.status === reportsFilter).length > 0 ? (
                  <div className="space-y-4 p-4">
                    {reports.filter(r => reportsFilter === "all" || r.status === reportsFilter).map((report) => (
                      <div 
                        key={report.report_id}
                        className={`p-4 rounded-lg border ${
                          report.status === 'pending' 
                            ? 'bg-yellow-500/10 border-yellow-500/30' 
                            : 'bg-slate-900/50 border-slate-800'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <span className={`badge-cockpit ${
                                report.status === 'pending' 
                                  ? 'text-yellow-400' 
                                  : report.status === 'dismissed'
                                    ? 'text-red-400'
                                    : 'text-emerald-400'
                              }`}>
                                {report.status === 'pending' 
                                  ? 'Pendente' 
                                  : report.status === 'dismissed'
                                    ? 'Dispensado'
                                    : 'Resolvido'}
                              </span>
                              <span className="badge-cockpit text-cyan-400">
                                {report.question?.section_name || 'Sem secção'}
                              </span>
                              <span className="text-xs text-slate-500">
                                Reportado por {report.reporter_name} em {new Date(report.created_at).toLocaleDateString('pt-PT')}
                              </span>
                            </div>
                            
                            {/* Question Statement */}
                            <p className="text-cockpit-text-bright mb-3">{report.question?.statement}</p>
                            
                            {/* Question Image */}
                            {report.question?.image && (
                              <img 
                                src={report.question.image} 
                                alt="Imagem da questão" 
                                className="mb-3 rounded-lg max-h-48 object-contain border border-slate-700"
                              />
                            )}
                            
                            {/* Question Answers */}
                            {report.question?.answers && (
                              <div className="mb-3 space-y-1">
                                {report.question.answers.map((ans, i) => {
                                  const ansText = ans?.text || ans;
                                  const ansId = ans?.id || `ans_${i}`;
                                  const isCorrect = ansId === report.question.correct_answer || i === report.question.correct_answer;
                                  
                                  return (
                                    <div 
                                      key={i}
                                      className={`text-xs px-2 py-1 rounded ${
                                        isCorrect
                                          ? 'bg-emerald-500/20 text-emerald-400' 
                                          : 'bg-slate-800/50 text-slate-400'
                                      }`}
                                    >
                                      <span className="font-mono mr-1">{['A', 'B', 'C', 'D'][i]}.</span>
                                      {ansText}
                                      {isCorrect && ' ✓'}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            
                            {/* Report Reason */}
                            <div className="bg-slate-900/50 p-2 rounded border-l-2 border-yellow-500">
                              <p className="text-xs text-slate-500 uppercase mb-1">Motivo da denúncia:</p>
                              <p className="text-sm text-slate-300">{report.reason || 'Sem motivo especificado'}</p>
                            </div>
                          </div>
                          
                          {report.status === 'pending' && (
                            <div className="flex flex-col gap-2">
                              {/* Edit Question Button */}
                              {report.question && (
                                <QuestionEditDialog 
                                  question={report.question} 
                                  sections={sections}
                                  onSave={fetchData}
                                  onDelete={fetchData}
                                  customTrigger={
                                    <Button 
                                      size="sm"
                                      className="bg-cyan-600 hover:bg-cyan-500"
                                      title="Editar questão"
                                    >
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                  }
                                />
                              )}
                              <Button 
                                size="sm"
                                onClick={() => resolveReport(report.report_id, 'resolved')}
                                className="bg-emerald-600 hover:bg-emerald-500"
                                title="Marcar como resolvido"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm"
                                variant="outline"
                                onClick={() => resolveReport(report.report_id, 'dismissed')}
                                className="btn-secondary-cockpit"
                                title="Dispensar"
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <AlertTriangle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Não há denúncias {reportsFilter !== "all" ? `${reportsFilter === "pending" ? "pendentes" : reportsFilter === "resolved" ? "resolvidas" : "dispensadas"}` : ""}</p>
                  </div>
                )}
              </ScrollArea>
            </Card>
          </TabsContent>

          {/* Rules Tab — System Rules / Config Engine */}
          <TabsContent value="rules" className="space-y-6">
            <RulesPanel />
          </TabsContent>

          {/* DI Tab — Direção de Instrução (Module 1: Controlo de Instrutores) */}
          <TabsContent value="di" className="space-y-4" data-testid="tab-content-di">
            <DiPanel />
          </TabsContent>

          {/* School Requests Tab — Phase 4 multi-tenancy */}
          <TabsContent value="school-requests" className="space-y-4" data-testid="tab-content-school-requests">
            <Card className="instrument-panel">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-chivo flex items-center gap-2 text-lg">
                    <ClipboardCheck className="w-5 h-5 text-amber-400" />
                    Pedidos de Associação a Escola
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    className="btn-secondary-cockpit"
                    onClick={refreshSchoolRequests}
                    data-testid="refresh-school-requests"
                  >
                    <RefreshCw className="w-3.5 h-3.5 mr-1" /> Atualizar
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {schoolRequests.length === 0 ? (
                  <div className="text-center py-10 text-slate-500 text-sm" data-testid="empty-school-requests">
                    <Inbox className="w-7 h-7 mx-auto mb-2 opacity-40" />
                    Sem pedidos pendentes.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {schoolRequests.map((req) => (
                      <SchoolRequestRow
                        key={req.user_id}
                        request={req}
                        classes={classes}
                        onApprove={approveSchoolRequest}
                        onReject={rejectSchoolRequest}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          {/* IA Stats Tab — super_admin only */}
          <TabsContent value="aerofleet" className="space-y-4" data-testid="tab-content-aerofleet">
            <AerofleetPanel />
          </TabsContent>

          <TabsContent value="diagnostics" className="space-y-4" data-testid="tab-content-diagnostics">
            <TrainingDiagnosticsPanel />
          </TabsContent>

          <TabsContent value="ia" className="space-y-4" data-testid="tab-content-ia">
            <AiStatsPanel />
          </TabsContent>
        </Tabs>
      </main>

      {/* Section Unlock Modal */}
      <Dialog open={unlockModalOpen} onOpenChange={setUnlockModalOpen}>
        <DialogContent className="bg-cockpit-panel border-cockpit-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-chivo flex items-center gap-2">
              <Unlock className="w-5 h-5 text-cyan-400" />
              Disciplinas de {selectedUserForUnlock?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {/* User Info Card */}
            {selectedUserForUnlock && (
              <div className="mb-4 p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                <div className="flex items-center gap-3 mb-2">
                  {selectedUserForUnlock.picture && (
                    <img src={selectedUserForUnlock.picture} alt="" className="w-10 h-10 rounded-full" />
                  )}
                  <div>
                    <p className="font-medium text-white">{selectedUserForUnlock.name}</p>
                    <p className="text-xs text-slate-400">{selectedUserForUnlock.email}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 text-xs">
                  {/* Subscription Status */}
                  <span className={`px-2 py-1 rounded-full ${
                    selectedUserForUnlock.subscription_status === "admin_granted" 
                      ? "bg-purple-500/20 text-purple-400 border border-purple-500/30" 
                      : selectedUserForUnlock.subscription_status === "premium"
                        ? "bg-yellow-500/20 text-yellow-400 border border-yellow-500/30"
                        : selectedUserForUnlock.subscription_status === "trial"
                          ? "bg-blue-500/20 text-blue-400 border border-blue-500/30"
                          : selectedUserForUnlock.subscription_status === "expired"
                            ? "bg-red-500/20 text-red-400 border border-red-500/30"
                            : "bg-slate-500/20 text-slate-400 border border-slate-500/30"
                  }`}>
                    {selectedUserForUnlock.subscription_status === "admin_granted" && "🟣 Acesso Admin (Ilimitado)"}
                    {selectedUserForUnlock.subscription_status === "premium" && `🟡 Premium (${selectedUserForUnlock.subscription_days_remaining} dias)`}
                    {selectedUserForUnlock.subscription_status === "trial" && `🔵 Trial (${selectedUserForUnlock.trial_days_remaining} dias)`}
                    {selectedUserForUnlock.subscription_status === "expired" && "🔴 Expirado"}
                    {selectedUserForUnlock.subscription_status === "free" && "⚪ Grátis"}
                  </span>
                  
                  {/* Expiry Date */}
                  {selectedUserForUnlock.subscription_ends_at && (
                    <span className="px-2 py-1 rounded-full bg-slate-700/50 text-slate-300">
                      📅 Expira: {new Date(selectedUserForUnlock.subscription_ends_at).toLocaleDateString('pt-PT')}
                    </span>
                  )}
                  {selectedUserForUnlock.trial_ends_at && selectedUserForUnlock.subscription_status === "trial" && (
                    <span className="px-2 py-1 rounded-full bg-slate-700/50 text-slate-300">
                      📅 Trial até: {new Date(selectedUserForUnlock.trial_ends_at).toLocaleDateString('pt-PT')}
                    </span>
                  )}
                </div>
              </div>
            )}
            
            <p className="text-sm text-slate-400 mb-4">
              Seleccione as disciplinas que este aluno pode aceder para treino.
              A disciplina de Legislação está sempre desbloqueada.
            </p>
            
            <div className="flex justify-end mb-4">
              {(() => {
                const allSectionIds = modalSections.map(s => s.section_id);
                const allUnlocked = allSectionIds.length > 0 && allSectionIds.every(id => userUnlockedSections.includes(id));
                
                return (
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={toggleAllSectionsForUser}
                    className={allUnlocked 
                      ? "border-red-500 text-red-400 hover:bg-red-500/10" 
                      : "border-cyan-500 text-cyan-400 hover:bg-cyan-500/10"
                    }
                  >
                    {allUnlocked ? (
                      <>
                        <Lock className="w-4 h-4 mr-2" />
                        Bloquear Todas
                      </>
                    ) : (
                      <>
                        <Unlock className="w-4 h-4 mr-2" />
                        Desbloquear Todas
                      </>
                    )}
                  </Button>
                );
              })()}
            </div>
            
            <ScrollArea className="h-[300px] pr-4">
              <div className="space-y-2">
                {modalSections.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <p>Nenhuma disciplina encontrada</p>
                  </div>
                ) : modalSections
                  .sort((a, b) => (a.section_name || '').localeCompare(b.section_name || '', 'pt'))
                  .map((s) => {
                    const isUnlocked = userUnlockedSections.includes(s.section_id);
                    const isLegislacao = s.section_id === "sec_legislacao";
                    
                    return (
                      <div 
                        key={s.section_id}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          isUnlocked 
                            ? 'bg-cyan-500/10 border-cyan-500/30' 
                            : 'bg-slate-800/50 border-slate-700'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {isUnlocked ? (
                            <Unlock className="w-4 h-4 text-cyan-400" />
                          ) : (
                            <Lock className="w-4 h-4 text-slate-500" />
                          )}
                          <div>
                            <div className={`text-sm ${isUnlocked ? 'text-cyan-300' : 'text-slate-400'}`}>
                              {s.section_name}
                            </div>
                            <div className="text-xs text-slate-500">
                              {s.question_count || 0} questões
                            </div>
                          </div>
                        </div>
                        
                        <Button
                          size="sm"
                          variant={isUnlocked ? "outline" : "default"}
                          onClick={() => toggleSectionUnlock(s.section_id, isUnlocked)}
                          disabled={isLegislacao}
                          className={isLegislacao ? "opacity-50 cursor-not-allowed" : ""}
                          title={isLegislacao ? "Legislação está sempre desbloqueada" : ""}
                        >
                          {isUnlocked ? (
                            <>
                              <Lock className="w-3 h-3 mr-1" />
                              Bloquear
                            </>
                          ) : (
                            <>
                              <Unlock className="w-3 h-3 mr-1" />
                              Desbloquear
                            </>
                          )}
                        </Button>
                      </div>
                    );
                  })}
              </div>
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Edit User Name Dialog */}
      <Dialog open={showEditNameDialog} onOpenChange={setShowEditNameDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-chivo text-cockpit-text-bright flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-orange-400" />
              Editar Nome do Utilizador
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="admin-user-name" className="text-slate-300">Nome</Label>
              <Input
                id="admin-user-name"
                value={editingUserName}
                onChange={(e) => setEditingUserName(e.target.value)}
                placeholder="Nome do utilizador"
                className="bg-slate-800 border-slate-700 text-white"
                onKeyDown={(e) => e.key === 'Enter' && handleSaveUserName()}
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowEditNameDialog(false)}
              className="border-slate-700 text-slate-300"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleSaveUserName}
              disabled={savingUserName || !editingUserName?.trim()}
              className="btn-cockpit"
            >
              {savingUserName ? "A guardar..." : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// School request row — Phase 4 multi-tenancy
const SchoolRequestRow = ({ request, classes, onApprove, onReject }) => {
  const [selectedClass, setSelectedClass] = useState("");
  const [busy, setBusy] = useState(false);

  // Filter classes for the requested school (super-admin can see classes from
  // any school; school admin only sees their own school's classes already).
  const availableClasses = (classes || []).filter(
    (c) => !c.school_id || c.school_id === request.requested_school_id,
  );

  const handleApprove = async () => {
    setBusy(true);
    try {
      await onApprove(request.user_id, selectedClass || null);
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async () => {
    setBusy(true);
    try {
      await onReject(request.user_id);
    } finally {
      setBusy(false);
    }
  };

  const requestedAt = request.requested_school_at
    ? new Date(request.requested_school_at).toLocaleString("pt-PT")
    : "";

  return (
    <div
      className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-lg border border-slate-800 bg-slate-900/40"
      data-testid={`school-request-${request.user_id}`}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {request.picture ? (
          <img src={request.picture} alt={request.name} className="w-9 h-9 rounded-full object-cover" />
        ) : (
          <div className="w-9 h-9 rounded-full bg-slate-700 flex items-center justify-center text-white text-sm font-bold">
            {request.name?.charAt(0)?.toUpperCase() || "?"}
          </div>
        )}
        <div className="min-w-0">
          <div className="text-sm font-medium text-cockpit-text-bright truncate">{request.name}</div>
          <div className="text-[11px] text-slate-500 font-mono truncate">{request.email}</div>
          <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-2">
            <School className="w-3 h-3" style={{ color: request.requested_school?.primary_color || "#f59e0b" }} />
            <span>Pediu: <span className="text-amber-300">{request.requested_school?.name || request.requested_school_id}</span></span>
          </div>
          {requestedAt && (
            <div className="text-[10px] text-slate-600 font-mono mt-0.5">{requestedAt}</div>
          )}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
        {availableClasses.length > 0 && (
          <Select value={selectedClass || "none"} onValueChange={(v) => setSelectedClass(v === "none" ? "" : v)}>
            <SelectTrigger className="w-full sm:w-[180px] bg-slate-950 border-slate-700" data-testid={`class-select-${request.user_id}`}>
              <SelectValue placeholder="Sem turma" />
            </SelectTrigger>
            <SelectContent className="bg-slate-950 border-slate-700">
              <SelectItem value="none">Sem turma</SelectItem>
              {availableClasses.map((c) => (
                <SelectItem key={c.class_id} value={c.class_id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Button
          size="sm"
          className="btn-cockpit"
          onClick={handleApprove}
          disabled={busy}
          data-testid={`approve-school-request-${request.user_id}`}
        >
          <CheckCircle className="w-3.5 h-3.5 mr-1" /> Aprovar
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="btn-secondary-cockpit text-red-400 hover:text-red-300"
          onClick={handleReject}
          disabled={busy}
          data-testid={`reject-school-request-${request.user_id}`}
        >
          <XCircle className="w-3.5 h-3.5 mr-1" /> Rejeitar
        </Button>
      </div>
    </div>
  );
};


const StatCard = ({ icon, value, label, color, onClick }) => {
  const colorClasses = {
    orange: "text-orange-400 bg-orange-500/10",
    cyan: "text-cyan-400 bg-cyan-500/10",
    red: "text-red-400 bg-red-500/10",
    slate: "text-slate-400 bg-slate-500/10",
  };

  return (
    <Card 
      className={`instrument-panel ${onClick ? 'cursor-pointer hover:border-slate-600 transition-colors' : ''}`}
      onClick={onClick}
    >
      <CardContent className="p-4 flex items-center gap-4">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
          {icon}
        </div>
        <div>
          <div className={`font-mono text-2xl ${colorClasses[color].split(' ')[0]}`}>{value}</div>
          <div className="text-xs text-slate-500">{label}</div>
        </div>
      </CardContent>
    </Card>
  );
};

const QuestionViewDialog = ({ question }) => {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="text-slate-400 hover:text-cyan-400">
          <Eye className="w-4 h-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-cockpit-panel border-cockpit-border max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-chivo">Detalhes da Questão</DialogTitle>
        </DialogHeader>
        <div className="mt-4 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500">Secção</label>
            <p className="text-cockpit-text-bright">{question.section_name}</p>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500">Enunciado</label>
            <p className="text-cockpit-text-bright">{question.statement}</p>
          </div>
          {question.image && (
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500">Imagem</label>
              <img 
                src={question.image} 
                alt="Imagem da questão" 
                className="mt-2 rounded-lg max-h-64 object-contain border border-slate-700"
                data-testid="view-question-image"
              />
            </div>
          )}
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500">Respostas</label>
            <div className="space-y-2 mt-2">
              {question.answers.map((ans, i) => {
                const ansText = ans?.text || ans;
                const ansId = ans?.id || `ans_${i}`;
                const isCorrect = ansId === question.correct_answer || i === question.correct_answer;
                
                return (
                  <div 
                    key={ansId}
                    className={`p-3 rounded-lg border ${
                      isCorrect
                        ? 'bg-emerald-500/10 border-emerald-500/50' 
                        : 'bg-slate-900/50 border-slate-800'
                    }`}
                  >
                    <span className="font-mono text-sm mr-2">{['A', 'B', 'C', 'D'][i]}.</span>
                    {ansText}
                    {isCorrect && (
                      <CheckCircle className="w-4 h-4 text-emerald-400 inline ml-2" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          {question.note && (
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500">Nota</label>
              <p className="text-slate-400">{question.note}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const QuestionEditDialog = ({ question, sections, onSave, onDelete, customTrigger }) => {
  const [open, setOpen] = useState(false);
  const [statement, setStatement] = useState(question.statement);
  const [sectionId, setSectionId] = useState(question.section_id || "");
  const [image, setImage] = useState(question.image || "");
  const [uploadingImage, setUploadingImage] = useState(false);
  // Handle both old format (strings) and new format (objects with id/text)
  const [answers, setAnswers] = useState(
    question.answers.map((ans, i) => ({
      id: ans?.id || `ans_${i}`,
      text: ans?.text || ans
    }))
  );
  // Handle both old format (int index) and new format (string id)
  const [correctAnswer, setCorrectAnswer] = useState(
    typeof question.correct_answer === 'string' 
      ? question.correct_answer 
      : `ans_${question.correct_answer}`
  );
  const [note, setNote] = useState(question.note || "");
  const [saving, setSaving] = useState(false);

  // Reset state from latest props when dialog opens
  const handleOpenChange = (isOpen) => {
    if (isOpen) {
      setStatement(question.statement);
      setSectionId(question.section_id || "");
      setImage(question.image || "");
      setAnswers(question.answers.map((ans, i) => ({
        id: ans?.id || `ans_${i}`,
        text: ans?.text || ans
      })));
      setCorrectAnswer(
        typeof question.correct_answer === 'string'
          ? question.correct_answer
          : `ans_${question.correct_answer}`
      );
      setNote(question.note || "");
    }
    setOpen(isOpen);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("file", await copiaDoFicheiro(file));
      const response = await axios.post(
        `${API}/admin/upload-image`,
        formData,
        { 
          withCredentials: true
        }
      );
      setImage(response.data.image_url);
      toast.success("Imagem carregada com sucesso");
    } catch (error) {
      toast.error(mensagemDeErro(error, "Erro ao carregar imagem"));
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = () => {
    setImage("");
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        statement,
        answers,
        correct_answer: correctAnswer,
        note: note || null,
        image: image || null
      };
      // Only include section_id if changed
      if (sectionId && sectionId !== question.section_id) {
        payload.section_id = sectionId;
      }
      await axios.put(
        `${API}/admin/questions/${question.question_id}`,
        payload,
        { withCredentials: true }
      );
      toast.success("Questão atualizada com sucesso");
      setOpen(false);
      onSave();
    } catch (error) {
      toast.error(mensagemDeErro(error, "Erro ao atualizar questão"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm("Tem a certeza que deseja eliminar esta questão? Esta ação não pode ser desfeita.")) return;
    
    try {
      await axios.delete(
        `${API}/admin/questions/${question.question_id}`,
        { withCredentials: true }
      );
      toast.success("Questão eliminada com sucesso");
      setOpen(false);
      onDelete();
    } catch (error) {
      toast.error("Erro ao eliminar questão");
    }
  };

  const updateAnswer = (index, value) => {
    const newAnswers = [...answers];
    newAnswers[index] = { ...newAnswers[index], text: value };
    setAnswers(newAnswers);
  };

  const addEditAnswer = () => {
    const newId = `ans_${answers.length}`;
    setAnswers([...answers, { id: newId, text: "" }]);
  };

  const removeEditAnswer = (index) => {
    if (answers.length <= 2) return;
    const removed = answers[index];
    const newAnswers = answers.filter((_, i) => i !== index);
    setAnswers(newAnswers);
    if (correctAnswer === removed.id) {
      setCorrectAnswer(newAnswers[0].id);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {customTrigger || (
          <Button variant="ghost" size="icon" className="text-slate-400 hover:text-yellow-400" title="Editar">
            <Edit className="w-4 h-4" />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="bg-cockpit-panel border-cockpit-border max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-chivo">Editar Questão</DialogTitle>
        </DialogHeader>
        <div className="mt-4 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500">Disciplina</label>
            <select
              value={sectionId}
              onChange={(e) => setSectionId(e.target.value)}
              className="input-cockpit mt-1 w-full"
              data-testid="edit-question-section"
            >
              {[...(sections || [])].sort((a, b) => a.section_name.localeCompare(b.section_name)).map((s) => (
                <option key={s.section_id} value={s.section_id}>{s.section_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500">Enunciado</label>
            <textarea
              value={statement}
              onChange={(e) => setStatement(e.target.value)}
              className="input-cockpit mt-1 w-full min-h-[100px] resize-y"
              placeholder="Enunciado da questão..."
            />
          </div>
          
          {/* Image Upload */}
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500">Imagem (opcional)</label>
            <div className="mt-2">
              {image ? (
                <div className="relative">
                  <img 
                    src={image} 
                    alt="Imagem da questão" 
                    className="max-h-48 rounded-lg border border-slate-700 object-contain"
                    data-testid="edit-question-image-preview"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRemoveImage}
                    className="absolute top-2 right-2 bg-red-500/80 hover:bg-red-500 text-white h-8 px-2"
                    data-testid="edit-question-remove-image"
                  >
                    <XCircle className="w-4 h-4 mr-1" />
                    Remover
                  </Button>
                </div>
              ) : (
                <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-600 rounded-lg cursor-pointer hover:border-slate-500 transition-colors">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={uploadingImage}
                    data-testid="edit-question-image-input"
                  />
                  {uploadingImage ? (
                    <>
                      <div className="w-5 h-5 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
                      <span className="text-slate-400">A carregar...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5 text-slate-400" />
                      <span className="text-slate-400">Clique para adicionar imagem</span>
                    </>
                  )}
                </label>
              )}
              <p className="text-xs text-slate-500 mt-1">Formatos: JPG, PNG, WEBP, GIF. Máx: 5MB</p>
            </div>
          </div>
          
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs uppercase tracking-wider text-slate-500">Respostas ({answers.length})</label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={addEditAnswer}
                disabled={answers.length >= 8}
                className="text-xs text-cyan-400 hover:text-cyan-300"
              >
                + Adicionar resposta
              </Button>
            </div>
            <div className="space-y-2 mt-2">
              {answers.map((ans, i) => (
                <div key={ans.id} className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setCorrectAnswer(ans.id)}
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-mono shrink-0 transition-colors ${
                      ans.id === correctAnswer 
                        ? 'bg-emerald-500 text-white' 
                        : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                    }`}
                    title={ans.id === correctAnswer ? "Resposta correta" : "Clique para definir como correta"}
                  >
                    {String.fromCharCode(65 + i)}
                  </button>
                  <Input
                    value={ans.text}
                    onChange={(e) => updateAnswer(i, e.target.value)}
                    className="input-cockpit flex-1"
                    placeholder={`Resposta ${String.fromCharCode(65 + i)}`}
                  />
                  {ans.id === correctAnswer && (
                    <CheckCircle className="w-5 h-5 text-emerald-400 shrink-0" />
                  )}
                  {answers.length > 2 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeEditAnswer(i)}
                      className="text-red-400 hover:text-red-300 shrink-0 px-1"
                      title="Remover resposta"
                    >
                      <XCircle className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-2">Clique na letra para definir a resposta correta</p>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500">Nota / Explicação (opcional)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="input-cockpit mt-1 w-full min-h-[60px] resize-y"
              placeholder="Explicação ou nota adicional..."
            />
          </div>
          <div className="flex gap-2 pt-4 border-t border-cockpit-border">
            <Button
              variant="outline"
              onClick={handleDelete}
              className="text-red-400 border-red-400/50 hover:bg-red-500/10"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Eliminar
            </Button>
            <div className="flex-1" />
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              className="btn-secondary-cockpit"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="btn-cockpit"
            >
              {saving ? "A guardar..." : "Guardar"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Component to create new questions with variable number of answers
const QuestionCreateDialog = ({ sections, onCreated }) => {
  const [open, setOpen] = useState(false);
  const [statement, setStatement] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [answers, setAnswers] = useState([
    { id: "ans_0", text: "" },
    { id: "ans_1", text: "" }
  ]);
  const [correctAnswer, setCorrectAnswer] = useState("ans_0");
  const [note, setNote] = useState("");
  const [image, setImage] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("file", await copiaDoFicheiro(file));
      const response = await axios.post(
        `${API}/admin/upload-image`,
        formData,
        { withCredentials: true }
      );
      setImage(response.data.image_url);
      toast.success("Imagem carregada com sucesso");
    } catch (error) {
      toast.error(mensagemDeErro(error, "Erro ao carregar imagem"));
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = () => {
    setImage("");
  };

  const resetForm = () => {
    setStatement("");
    setSectionId("");
    setAnswers([
      { id: "ans_0", text: "" },
      { id: "ans_1", text: "" }
    ]);
    setCorrectAnswer("ans_0");
    setNote("");
    setImage("");
  };

  const handleCreate = async () => {
    // Validation
    if (!statement.trim()) {
      toast.error("O enunciado é obrigatório");
      return;
    }
    if (!sectionId) {
      toast.error("Selecione uma secção/disciplina");
      return;
    }
    if (answers.length < 2) {
      toast.error("Adicione pelo menos 2 respostas");
      return;
    }
    const emptyAnswers = answers.filter(a => !a.text.trim());
    if (emptyAnswers.length > 0) {
      toast.error("Todas as respostas devem ter texto");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(
        `${API}/admin/questions`,
        {
          section_id: sectionId,
          statement: statement.trim(),
          answers: answers.map(a => ({ id: a.id, text: a.text.trim() })),
          correct_answer: correctAnswer,
          note: note.trim() || null,
          image: image || null
        },
        { withCredentials: true }
      );
      toast.success(`Questão criada com sucesso! (${response.data.section_name})`);
      setOpen(false);
      resetForm();
      onCreated();
    } catch (error) {
      console.error("Error creating question:", error);
      toast.error(mensagemDeErro(error, "Erro ao criar questão"));
    } finally {
      setSaving(false);
    }
  };

  const addAnswer = () => {
    const newId = `ans_${answers.length}`;
    setAnswers([...answers, { id: newId, text: "" }]);
  };

  const removeAnswer = (index) => {
    if (answers.length <= 2) {
      toast.error("Mínimo de 2 respostas");
      return;
    }
    const removed = answers[index];
    const newAnswers = answers.filter((_, i) => i !== index);
    // Re-index answers
    const reindexed = newAnswers.map((a, i) => ({ id: `ans_${i}`, text: a.text }));
    setAnswers(reindexed);
    
    // Fix correct answer if needed
    if (correctAnswer === removed.id) {
      setCorrectAnswer("ans_0");
    } else {
      // Find new index of correct answer
      const oldCorrectIndex = answers.findIndex(a => a.id === correctAnswer);
      if (oldCorrectIndex > index) {
        setCorrectAnswer(`ans_${oldCorrectIndex - 1}`);
      }
    }
  };

  const updateAnswer = (index, value) => {
    const newAnswers = [...answers];
    newAnswers[index] = { ...newAnswers[index], text: value };
    setAnswers(newAnswers);
  };

  const letters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      setOpen(isOpen);
      if (!isOpen) resetForm();
    }}>
      <DialogTrigger asChild>
        <Button className="btn-cockpit" data-testid="create-question-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nova Questão
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-cockpit-panel border-cockpit-border max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-chivo">Criar Nova Questão</DialogTitle>
        </DialogHeader>
        <div className="mt-4 space-y-4">
          {/* Section Selection */}
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500">Disciplina *</label>
            <Select value={sectionId} onValueChange={setSectionId}>
              <SelectTrigger className="input-cockpit mt-1" data-testid="create-section-select">
                <SelectValue placeholder="Selecione uma disciplina" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                {[...sections]
                  .sort((a, b) => (a.section_name || '').localeCompare(b.section_name || '', 'pt'))
                  .map((s) => (
                    <SelectItem key={s.section_id} value={s.section_id}>
                      {s.section_name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          {/* Statement */}
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500">Enunciado *</label>
            <textarea
              value={statement}
              onChange={(e) => setStatement(e.target.value)}
              className="input-cockpit mt-1 w-full min-h-[100px] resize-y"
              placeholder="Escreva o enunciado da questão..."
              data-testid="create-statement"
            />
          </div>

          {/* Image Upload */}
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500">Imagem (opcional)</label>
            <div className="mt-2">
              {image ? (
                <div className="relative">
                  <img 
                    src={image} 
                    alt="Imagem da questão" 
                    className="max-h-48 rounded-lg border border-slate-700 object-contain"
                    data-testid="create-question-image-preview"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRemoveImage}
                    className="absolute top-2 right-2 bg-red-500/80 hover:bg-red-500 text-white h-8 px-2"
                    data-testid="create-question-remove-image"
                  >
                    <XCircle className="w-4 h-4 mr-1" />
                    Remover
                  </Button>
                </div>
              ) : (
                <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-600 rounded-lg cursor-pointer hover:border-slate-500 transition-colors">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={uploadingImage}
                    data-testid="create-question-image-input"
                  />
                  {uploadingImage ? (
                    <>
                      <div className="w-5 h-5 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
                      <span className="text-slate-400">A carregar...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5 text-slate-400" />
                      <span className="text-slate-400">Clique para adicionar imagem</span>
                    </>
                  )}
                </label>
              )}
              <p className="text-xs text-slate-500 mt-1">Formatos: JPG, PNG, WEBP, GIF. Máx: 5MB</p>
            </div>
          </div>

          {/* Answers */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs uppercase tracking-wider text-slate-500">
                Respostas ({answers.length})
              </label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addAnswer}
                disabled={answers.length >= 8}
                className="btn-secondary-cockpit text-xs"
              >
                <Plus className="w-3 h-3 mr-1" />
                Adicionar Resposta
              </Button>
            </div>
            <div className="space-y-2">
              {answers.map((ans, i) => (
                <div key={ans.id} className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setCorrectAnswer(ans.id)}
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-mono shrink-0 transition-colors ${
                      ans.id === correctAnswer 
                        ? 'bg-emerald-500 text-white' 
                        : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                    }`}
                    title={ans.id === correctAnswer ? "Resposta correta" : "Clique para definir como correta"}
                  >
                    {letters[i]}
                  </button>
                  <Input
                    value={ans.text}
                    onChange={(e) => updateAnswer(i, e.target.value)}
                    className="input-cockpit flex-1"
                    placeholder={`Resposta ${letters[i]}`}
                    data-testid={`create-answer-${i}`}
                  />
                  {ans.id === correctAnswer && (
                    <CheckCircle className="w-5 h-5 text-emerald-400 shrink-0" />
                  )}
                  {answers.length > 2 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeAnswer(i)}
                      className="text-slate-400 hover:text-red-400 shrink-0"
                      title="Remover resposta"
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Clique na letra para definir a resposta correta. Mínimo 2, máximo 8 respostas.
            </p>
          </div>

          {/* Note */}
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500">Nota / Explicação (opcional)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="input-cockpit mt-1 w-full min-h-[60px] resize-y"
              placeholder="Explicação ou nota adicional..."
              data-testid="create-note"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-4 border-t border-cockpit-border">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              className="btn-secondary-cockpit"
            >
              Cancelar
            </Button>
            <div className="flex-1" />
            <Button
              onClick={handleCreate}
              disabled={saving || !statement.trim() || !sectionId}
              className="btn-cockpit"
              data-testid="create-question-submit"
            >
              {saving ? "A criar..." : "Criar Questão"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AdminDashboard;
